<template>
    <central-modal
        v-if="promptReadyToMount"
        v-model:visible="modalVisible"
        header="Personalise your feed"
        size="small"
        @hide="handleHide"
    >
        <div class="site-area-relevance-prompt">
            <div class="prompt-intro">
                <font-awesome-icon :icon="['fas', 'users']" />
                <div>
                    <h3>Hello!</h3>
                    <p>
                        Please select at least one site area you are interested in. This will help us tailor the content we show you to fit your interests.
                    </p>
                </div>
            </div>
            <site-area-relevance-selector
                v-model="selectedInterestAreas"
                :options="siteAreaRelevanceOptions"
                require-at-least-one
            />
        </div>
        <template #footer>
            <div class="prompt-footer-actions">
                <primevue-button class="plain-btn" :disabled="isSaving" @click="dismissPrompt">
                    Later
                    <font-awesome-icon :icon="['fas', 'xmark']" />
                </primevue-button>
                <primevue-button
                    :disabled="!selectedInterestAreas.length || isSaving"
                    @click="saveInterestAreas"
                >
                    <component-loader v-if="isSaving" class="save-loader" />
                    <template v-else>
                        Save
                        <font-awesome-icon :icon="['fas', 'floppy-disk']" />
                    </template>
                </primevue-button>
            </div>
        </template>
    </central-modal>
</template>

<script setup lang="ts">
import {
    computed, nextTick, ref, watch,
} from 'vue';
import { authState } from '@lcp/http-auth';
import CentralModal from '@/components/common/CentralModal.vue';
import ComponentLoader from '@/components/layout/ComponentLoader.vue';
import SiteAreaRelevanceSelector from '@/components/community/SiteAreaRelevanceSelector.vue';
import { useDeltaUserSettingsStore } from '@/stores/deltaGpt/deltaGptUserSettingsStore.ts';
import { useUIStore } from '@/stores/uiStore.ts';
import PrimevueButton from 'primevue/button';
import { SITE_AREA_RELEVANCE_OPTIONS } from '@/utils/siteAreaRelevanceUtils.ts';

const deltaUserSettingsStore = useDeltaUserSettingsStore();
const uiStore = useUIStore();

const modalVisible = ref<boolean>(false);
const isSaving = ref<boolean>(false);
const siteAreaRelevanceOptions = SITE_AREA_RELEVANCE_OPTIONS;
const selectedInterestAreas = ref<Array<string>>([]);

const promptReadyToMount = computed<boolean>(() => (
    !uiStore.showPageLoader
));

const shouldShowPrompt = computed<boolean>(() => (
    authState.value.loggedIn
    && promptReadyToMount.value
    && !deltaUserSettingsStore.interestAreaPromptDismissedThisSession
    && deltaUserSettingsStore.userSettings.interestAreas.length === 0
));

async function saveInterestAreas (): Promise<void> {
    if (!selectedInterestAreas.value.length) return;

    isSaving.value = true;
    try {
        await deltaUserSettingsStore.updateUserSettings({ interestAreas: selectedInterestAreas.value });
        deltaUserSettingsStore.dismissInterestAreaPromptForSession();
        modalVisible.value = false;
    } finally {
        isSaving.value = false;
    }
}

function dismissPrompt (): void {
    deltaUserSettingsStore.dismissInterestAreaPromptForSession();
    modalVisible.value = false;
}

function handleHide (): void {
    if (deltaUserSettingsStore.userSettings.interestAreas.length > 0) return;
    deltaUserSettingsStore.dismissInterestAreaPromptForSession();
}

watch(shouldShowPrompt, async (shouldShow) => {
    if (!shouldShow) return;
    selectedInterestAreas.value = [];
    await nextTick();
    modalVisible.value = true;
}, { immediate: true });

</script>

<style scoped lang="scss">
.site-area-relevance-prompt {
    display: flex;
    flex-direction: column;
    gap: var(--extraLargeSpacing);
    min-height: 28rem;
    padding: var(--extraLargeSpacing);

    p {
        margin: 0;
        color: var(--neutral700);
    }
}

.prompt-intro {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: var(--largeSpacing);
    padding: var(--largeSpacing);
    border: 1px solid var(--neutral300);
    border-radius: var(--borderRadiusOuter);
    background: var(--neutral200);

    svg {
        color: var(--accentColour);
        font-size: var(--extraLargeFontSize);
    }

    h3 {
        margin: 0 0 var(--smallSpacing);
        color: var(--primaryFont);
        font-size: var(--largeFontSize);
    }
}

.options-loader {
    height: 6rem;
}

.options-error {
    color: var(--dangerColour);
}

.prompt-footer-actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--defaultSpacing);
    width: 100%;
}

.save-loader {
    height: 2.4rem;
    width: 4rem;
}
</style>
